import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const LOGO_URL = "/logo.png";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error, manual
  const [manualToken, setManualToken] = useState("");
  const [loading, setLoading] = useState(false);

  const tokenFromUrl = searchParams.get("token");

  useEffect(() => {
    if (tokenFromUrl) {
      verifyToken(tokenFromUrl);
    } else {
      setStatus("manual");
    }
  }, [tokenFromUrl]);

  const verifyToken = async (token) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      setStatus("success");
      toast.success("Email verified successfully!");
    } catch (error) {
      setStatus("error");
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerify = async (e) => {
    e.preventDefault();
    if (!manualToken.trim()) {
      toast.error("Please enter the verification token");
      return;
    }
    verifyToken(manualToken.trim());
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        <img 
          src={LOGO_URL} 
          alt="Credit2Cash" 
          className="h-20 mx-auto logo-glow mb-8"
          data-testid="verify-logo"
        />

        <div className="premium-card rounded-2xl p-8">
          {status === "verifying" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#00F0FF]/20 flex items-center justify-center mb-6">
                <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Verifying Email...</h2>
              <p className="text-[#A1A1AA]">Please wait while we verify your email address.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF66]/20 flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Email Verified!</h2>
              <p className="text-[#A1A1AA] mb-6">
                Your email has been successfully verified. You can now log in to your account.
              </p>
              <Link to="/login">
                <Button 
                  className="w-full bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-[#FF007F]/20 flex items-center justify-center mb-6">
                <XCircle className="w-10 h-10 text-[#FF007F]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Verification Failed</h2>
              <p className="text-[#A1A1AA] mb-6">
                The verification link is invalid or has expired. Please try again or request a new verification email.
              </p>
              <div className="space-y-4">
                <Button 
                  onClick={() => setStatus("manual")}
                  className="w-full bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                  data-testid="try-manual-btn"
                >
                  Enter Token Manually
                </Button>
                <Link to="/register">
                  <Button 
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10"
                  >
                    Register Again
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "manual" && (
            <>
              <h2 className="text-2xl font-bold text-white mb-4">Enter Verification Token</h2>
              <p className="text-[#A1A1AA] mb-6">
                Paste the verification token from your email below.
              </p>
              <form onSubmit={handleManualVerify} className="space-y-4">
                <Input
                  type="text"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="Paste your verification token"
                  className="input-dark"
                  data-testid="manual-token-input"
                />
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00FF66] text-black font-bold hover:bg-[#00FF66]/90"
                  data-testid="verify-token-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    "Verify Token"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-[#A1A1AA]">
          Need help?{" "}
          <a href="mailto:cash@credit2cash.store" className="text-[#00FF66] hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
